import React, { useState } from 'react';
import Icon from '@mdi/react';
import { mdiClose, mdiClockOutline, mdiCalendarClock, mdiCheckCircle, mdiCloseCircle, mdiAirplaneTakeoff, mdiCloseCircleOutline } from '@mdi/js';
import { formatCurrencyFromMinor } from '../utils/currency';
import './TimeSlotPickerModal.css';

/**
 * Time slot selection modal for booking flow - MATCHES MOBILE APP.
 *
 * Key Features:
 * - Multi-select time slots (can select multiple non-contiguous slots)
 * - Travel banner (if instructor is traveling to that location)
 * - Running total footer with price calculation
 * - Cancel/Confirm buttons in footer
 *
 * Matches mobile app styling: plain white modal, teal borders on slots.
 *
 * @param {boolean} isOpen - Modal visibility
 * @param {function} onClose - Close handler (Cancel button)
 * @param {object} selectedDay - Day object from PublicBookingCalendar
 *   - date: Luxon DateTime
 *   - dateKey: ISO date string
 *   - slots: Array of {startTime, endTime, status}
 *   - travelPeriod: Optional {destination, timezone, timezoneOffset, color}
 * @param {number} hourlyRate - Instructor's hourly rate (e.g., 65)
 * @param {string} currency - Currency code (e.g., "AUD")
 * @param {function} onConfirm - Callback when Confirm button clicked with (selectedSlots)
 * @param {boolean} hidePrice - Hide the running total (mascot demo instructor: the tip is chosen next)
 */
const TimeSlotPickerModal = ({
  isOpen,
  onClose,
  selectedDay,
  hourlyRate = 0,
  currency = 'AUD',
  onConfirm,
  hidePrice = false
}) => {
  const [selectedSlots, setSelectedSlots] = useState([]);

  if (!isOpen || !selectedDay) return null;

  // Sort all slots by start time (booked slots shown as non-clickable, matching mobile)
  const sortedSlots = [...selectedDay.slots].sort((a, b) => {
    return a.startTime.localeCompare(b.startTime);
  });

  // Format date display
  const formattedDate = selectedDay.date.toFormat('EEEE, MMMM d, yyyy');

  // Calculate duration from time strings (returns minutes)
  const calculateDuration = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    return durationMinutes;
  };

  // Format time for display (24hr to 12hr with am/pm)
  const formatTimeRange = (startTime, endTime) => {
    const formatTime = (time) => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'pm' : 'am';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes}${ampm}`;
    };

    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  // Check if slot is selected
  const isSlotSelected = (slot) => {
    return selectedSlots.some(
      s => s.startTime === slot.startTime && s.endTime === slot.endTime
    );
  };

  // Handle slot click (multi-select toggle — booked slots are non-interactive)
  const handleSlotClick = (slot) => {
    if (slot.status === 'booked') return;
    const selected = isSlotSelected(slot);

    let newSelection;
    if (selected) {
      // Remove from selection
      newSelection = selectedSlots.filter(
        s => !(s.startTime === slot.startTime && s.endTime === slot.endTime)
      );
    } else {
      // Add to selection
      const duration = calculateDuration(slot.startTime, slot.endTime);
      newSelection = [...selectedSlots, {
        startTime: slot.startTime,
        endTime: slot.endTime,
        duration
      }];
    }

    setSelectedSlots(newSelection);
  };

  // Calculate total price (hourly rate × number of slots)
  const totalPrice = selectedSlots.length * hourlyRate;

  // Handle Cancel - Clear selection and close
  const handleCancel = () => {
    setSelectedSlots([]);
    onClose();
  };

  // Handle Confirm - Pass selected slots to parent
  const handleConfirmClick = () => {
    if (selectedSlots.length === 0) return;
    onConfirm(selectedSlots);
  };

  // Format timezone display (e.g., "Australia/Adelaide (UTC+10:30)")
  const formatTimezone = (timezone, offset) => {
    if (!timezone) return '';
    return `${timezone}${offset ? ` (UTC${offset})` : ''}`;
  };

  return (
    <div className="time-slot-picker-overlay" onClick={handleCancel}>
      <div className="time-slot-picker-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header - Plain white, no purple background */}
        <div className="time-slot-picker-header">
          <div className="time-slot-header-content">
            <Icon path={mdiCalendarClock} size={1} className="time-slot-header-icon" />
            <div className="time-slot-header-text">
              <h2>Select Available Lessons</h2>
              <p className="time-slot-date">{formattedDate}</p>
            </div>
          </div>
          <button className="time-slot-picker-close" onClick={handleCancel} aria-label="Close">
            <Icon path={mdiClose} size={1} />
          </button>
        </div>

        {/* Travel Banner (if traveling to this location) */}
        {selectedDay.travelPeriod && (
          <div className="time-slot-travel-banner" style={{
            backgroundColor: `${selectedDay.travelPeriod.color}1A`, // 10% opacity
            borderLeftColor: selectedDay.travelPeriod.color
          }}>
            <Icon
              path={mdiAirplaneTakeoff}
              size={0.9}
              style={{ color: selectedDay.travelPeriod.color }}
              className="travel-banner-icon"
            />
            <div className="travel-banner-text">
              <div className="travel-banner-message">
                Travel availability for {selectedDay.travelPeriod.destination}
              </div>
              {selectedDay.travelPeriod.timezone && (
                <div className="travel-banner-timezone">
                  {formatTimezone(
                    selectedDay.travelPeriod.timezone,
                    selectedDay.travelPeriod.timezoneOffset
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Body - Scrollable Slot List */}
        <div className="time-slot-picker-body">
          {sortedSlots.length === 0 ? (
            <div className="time-slot-empty">
              <Icon path={mdiCloseCircle} size={2} className="time-slot-empty-icon" />
              <p>No available time slots for this day.</p>
            </div>
          ) : (
            <div className="time-slot-list">
              {sortedSlots.map((slot, index) => {
                const isBooked = slot.status === 'booked';
                const isSelected = isSlotSelected(slot);
                const formattedTime = formatTimeRange(slot.startTime, slot.endTime);

                let badgeIcon = mdiClockOutline;
                let badgeText = 'Available';
                if (isBooked) { badgeIcon = mdiCloseCircleOutline; badgeText = 'Booked'; }
                else if (isSelected) { badgeIcon = mdiCheckCircle; badgeText = 'Selected'; }

                return (
                  <button
                    key={index}
                    className={`time-slot-item ${isBooked ? 'booked' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSlotClick(slot)}
                    disabled={isBooked}
                  >
                    <div className="time-slot-content">
                      <div className="time-slot-left">
                        <div className="time-slot-time">{formattedTime}</div>
                      </div>
                      <div className="time-slot-right">
                        <div className="time-slot-badge">
                          <Icon
                            path={badgeIcon}
                            size={0.7}
                            className="time-slot-badge-icon"
                          />
                          <span className="time-slot-badge-text">
                            {badgeText}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Running Total + Cancel/Confirm Buttons */}
        <div className="time-slot-picker-footer">
          {!hidePrice && (
            <div className="time-slot-price-container">
              <span className="time-slot-price-label">Total:</span>
              <span className="time-slot-price-amount">
                {formatCurrencyFromMinor(totalPrice, currency)}
              </span>
              <span className="time-slot-price-details">
                ({selectedSlots.length} slot{selectedSlots.length !== 1 ? 's' : ''})
              </span>
            </div>
          )}
          <div className="time-slot-button-container">
            <button
              className="time-slot-btn-cancel"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              className="time-slot-btn-confirm"
              onClick={handleConfirmClick}
              disabled={selectedSlots.length === 0}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSlotPickerModal;
